import {
  ButtonComponentActionResultTypenames,
  Maybe,
  MyCashbackComponentResultTypenames,
  MyOffersComponentResultTypenames,
  OfferSource,
  PartnerOfferTopCategory,
  PerkSectionsData,
  PerkSectionsDataItemsResultTypenames,
  PromoSectionData,
  Query,
  SectionItems,
  SectionStyleType,
} from '@varo/graphql-schema-types';
import { Href, RoutePath, router } from 'expo-router';
import { PROMO_CARD_IDS } from 'features/rewards/constants/constants';
import { rewardsActionNavigation } from 'features/rewards/helpers/DetermineDestination.helpers';
import { REWARDS_HOME } from 'features/rewards/RewardsHome/RewardsHome.constants';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { ListItemProps } from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLeadingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemLeadingAccessory';
import { ListItemTrailingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemTrailingAccessory';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import { SectionedContentScreenSection } from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import {
  VXLComponent,
  VXLComponentId,
  VXLGroupId,
  VXLNode,
} from 'shared/renderer/types';
import theme from 'shared/styles/theme';

export const buildCashbackSection = (
  cashbackEarnedData?: Query,
): SectionedContentScreenSection => {
  const cashbackEarned =
    cashbackEarnedData?.myPartnerOffers?.myCashbackComponent;
  if (
    !cashbackEarned ||
    cashbackEarned.__typename !==
      MyCashbackComponentResultTypenames.MyCashbackComponentSuccess
  ) {
    return { sectionHeader: undefined, sectionFooter: undefined, data: [] };
  }

  return {
    sectionHeader: undefined,
    sectionFooter: undefined,
    data: [
      {
        id: 'cashbackEarned',
        name: VXLComponentId.CASHBACK_EARNED_CARD,
        props: {
          titleText: cashbackEarned.myCashbackComponent?.title ?? '',
          cashbackBalanceText:
            cashbackEarned.myCashbackComponent?.yearToDateEarnings?.formatted ??
            '',
          captionText: cashbackEarned.myCashbackComponent?.subtitle ?? '',
          onCardPress: () => {
            if (cashbackEarned.myCashbackComponent?.onClick?.route) {
              router.push(
                cashbackEarned.myCashbackComponent.onClick.route as RoutePath,
              );
            }
          },
          analyticsId: 'myCashbackClicked',
          customAttributes: cashbackEarned.myCashbackComponent?.onClick
            ?.analyticsEvent
            ? parseGQLAnalyticsEvent(
                cashbackEarned.myCashbackComponent.onClick.analyticsEvent,
              ).properties
            : null,
        },
      },
    ],
  };
};

export const buildActivatedOffersSection = (
  activatedOffersData?: Query,
  coordinates?: google.maps.LatLngLiteral,
): SectionedContentScreenSection => {
  const activatedOffers =
    activatedOffersData?.myPartnerOffers?.myOffersComponent;

  if (
    !activatedOffers ||
    activatedOffers.__typename !==
      MyOffersComponentResultTypenames.MyOffersComponentSuccess
  ) {
    return { sectionHeader: undefined, sectionFooter: undefined, data: [] };
  }

  return {
    sectionHeader: undefined,
    sectionFooter: undefined,
    data: [
      {
        id: 'activatedOffers',
        name: VXLComponentId.MY_OFFERS_CARD,
        props: {
          titleText: activatedOffers.myOffersComponent?.title ?? '',
          captionText: activatedOffers.myOffersComponent?.subTitle ?? '',
          bodyItems: activatedOffers.myOffersComponent?.bodyItems ?? [],
          onCardPress: () => {
            if (activatedOffers.onClick?.route) {
              if (coordinates) {
                router.push({
                  pathname: activatedOffers.onClick.route,
                  params: {
                    lat: String(coordinates.lat),
                    lng: String(coordinates.lng),
                  },
                } as Href);
              } else {
                router.push(activatedOffers.onClick.route as RoutePath);
              }
            }
          },
          analyticsId: 'myOffersClicked',
          customAttributes: activatedOffers.onClick?.analyticsEvent
            ? parseGQLAnalyticsEvent(activatedOffers.onClick.analyticsEvent)
                .properties
            : null,
        },
      },
    ],
  };
};

export const buildTopCategoriesSection = (
  topCategoriesData?: Query,
): SectionedContentScreenSection => {
  const topCategories = topCategoriesData?.myPartnerOffers?.topCategories;

  if (!topCategories) {
    return { sectionHeader: undefined, sectionFooter: undefined, data: [] };
  }

  return {
    sectionHeader: undefined,
    sectionFooter: undefined,
    data: [
      {
        id: 'topCategories',
        name: VXLGroupId.CATEGORY_GRID,
        props: {
          categories: topCategories.map(
            (category: PartnerOfferTopCategory) => ({
              label: category.name ?? '',
              iconKey: category.iconKey ?? 'ELLIPSE',
              iconColor: category.iconKey
                ? theme.color.text.primary
                : theme.color.interactive.secondaryPressed,
              onPress: () => {
                router.push({
                  pathname: '/rewards/categories/[category]',
                  params: {
                    category: category.id,
                    name: category.name,
                  },
                });
              },
              isDisabled: false,
              analyticsId: 'offerTypeSelected',
              customAttributes: category?.onClick?.analyticsEvent
                ? parseGQLAnalyticsEvent(category.onClick.analyticsEvent)
                    .properties
                : null,
            }),
          ),
          onPressViewAll: () => {
            router.push('/rewards/categories');
          },
        },
      },
    ],
  };
};

export const buildPromoCardSection = (
  promoSectionData?: Maybe<PromoSectionData>,
): SectionedContentScreenSection => {
  const promoCards: VXLNode[] = [];

  if (
    promoSectionData &&
    promoSectionData.id === PROMO_CARD_IDS.PROMO_CARD_BOOST
  ) {
    promoCards.push({
      id: promoSectionData.id,
      name: VXLComponentId.BOOST_PROMO_CARD,
      props: promoSectionData,
    });
  }

  return {
    sectionHeader: undefined,
    sectionFooter: undefined,
    data: promoCards,
  };
};

const minimalGridListItem = (
  sectionItem: SectionItems,
  sectionName: string,
  coordinates?: google.maps.LatLngLiteral,
): VXLComponent => {
  const trailingAccessory: ListItemTrailingAccessoryProps | undefined =
    sectionItem.cashback
      ? {
          id: `trailingAccessory${sectionItem.id}`,
          name: VXLComponentId.TAG,
          props: {
            text: sectionItem.cashback,
            type: TagType.INFO,
          },
        }
      : undefined;

  const leadingAccessory: ListItemLeadingAccessoryProps = {
    id: `leadingAccessory${sectionItem.id}`,
    name: VXLComponentId.ICON_AVATAR,
    props: {
      variant: 'MD',
      imageSource: { uri: sectionItem.logoImage },
    },
  };

  const props: ListItemProps = {
    primaryCopy: sectionItem.title,
    secondaryCopy: sectionItem.subTitle,
    showsChevron: false,
    leadingAccessory,
    trailingAccessory,
    onPress: sectionItem.onClick
      ? () => {
          rewardsActionNavigation(sectionItem, sectionName, coordinates);
        }
      : undefined,
    analyticsId: 'partnerOfferSelected',
    customAttributes: sectionItem.onClick?.analyticsEvent
      ? parseGQLAnalyticsEvent(sectionItem.onClick.analyticsEvent).properties
      : null,
  };

  return {
    id: sectionItem.id,
    name: VXLComponentId.LIST_ITEM,
    props: props,
  };
};

export const buildPerkSections = (
  sections?: PerkSectionsData[],
  coordinates?: google.maps.LatLngLiteral,
  activateButtonLabel?: string,
  nearbyRenderer?: (section: PerkSectionsData) => VXLNode[],
  translatedText?: (key: string, options?: Record<string, string>) => string,
): SectionedContentScreenSection[] => {
  if (!sections) {
    return [];
  }
  const rewardsSections = (section: PerkSectionsData): VXLNode[] => {
    switch (section.sectionStyleType) {
      case SectionStyleType.FeaturedCarousel:
        return [
          {
            id: section.id,
            name: VXLGroupId.FEATURED_OFFER_CARD_CAROUSEL,
            props: {
              offers: (section.itemsV2 ?? [])
                .filter(
                  (item): item is SectionItems =>
                    item.__typename ===
                    PerkSectionsDataItemsResultTypenames.SectionItems,
                )
                .map((item) => ({
                  id: item.id,
                  titleText:
                    item.subTitle && translatedText
                      ? translatedText('featuredOfferTitle', {
                          subTitle: item.subTitle,
                          title: item.title,
                        })
                      : item.title,
                  logoImage: item.logoImage
                    ? { uri: item.logoImage }
                    : undefined,
                  backgroundImage: item.heroImage
                    ? { uri: item.heroImage }
                    : undefined,
                  activateButtonLabel: activateButtonLabel ?? '',
                  onPress: item.onClick
                    ? () =>
                        rewardsActionNavigation(item, section.name, coordinates)
                    : undefined,
                  analyticsId: 'partnerOfferSelected',
                  customAttributes: item.onClick?.analyticsEvent
                    ? parseGQLAnalyticsEvent(item.onClick.analyticsEvent)
                        .properties
                    : null,
                })),
            },
          },
        ];
      case SectionStyleType.StandardCarousel:
        return [
          {
            id: section.id,
            name: VXLGroupId.STANDARD_OFFER_CARD_CAROUSEL,
            props: {
              offers: (section.itemsV2 ?? [])
                .filter(
                  (item): item is SectionItems =>
                    item.__typename ===
                    PerkSectionsDataItemsResultTypenames.SectionItems,
                )
                .map((item) => {
                  const isUpsideOffer = item.offerSource === OfferSource.Upside;
                  const isKardOffer = item.offerSource === OfferSource.Kard;

                  const resolvedCtaButtonLabel =
                    isUpsideOffer && !item.isActivated
                      ? item.ctaButton?.buttonText
                      : undefined;

                  const resolvedCtaLinkLabel = isKardOffer
                    ? item.ctaLabel
                    : undefined;

                  const ctaButtonOnClick = item.ctaButton?.buttonOnClick;
                  const ctaAnalytics =
                    ctaButtonOnClick?.__typename ===
                      ButtonComponentActionResultTypenames.ActivateOfferAction &&
                    ctaButtonOnClick.analyticsEvent
                      ? parseGQLAnalyticsEvent(ctaButtonOnClick.analyticsEvent)
                      : null;

                  return {
                    id: item.id,
                    titleText: item.title,
                    captionText: item.subTitle ?? '',
                    logoImage: item.logoImage
                      ? { uri: item.logoImage }
                      : undefined,
                    isLocationBased: !!item.distanceText,
                    isActivatedOffer: item.isActivated,
                    activatedDurationText: item.timeRemaining ?? '',
                    distanceText: isUpsideOffer ? item.distanceText : undefined,
                    onPress: item.onClick
                      ? () =>
                          rewardsActionNavigation(
                            item,
                            section.name,
                            coordinates,
                          )
                      : undefined,
                    analyticsId: 'partnerOfferSelected',
                    customAttributes: item.onClick?.analyticsEvent
                      ? parseGQLAnalyticsEvent(item.onClick.analyticsEvent)
                          .properties
                      : null,
                    ctaLinkLabel: resolvedCtaLinkLabel,
                    ctaButtonAnalyticsId: 'activateOfferClicked',
                    ctaButtonCustomAttributes: ctaAnalytics?.properties ?? null,
                    ctaButtonLabel: resolvedCtaButtonLabel,
                    onCtaButtonPress: resolvedCtaButtonLabel
                      ? () => {
                          router.push({
                            pathname: '/rewards/upside-offers/[merchantId]',
                            params: {
                              offerid: item.id,
                              merchantId: item.merchantId ?? '',
                              ...(coordinates && {
                                lat: String(coordinates.lat),
                                lng: String(coordinates.lng),
                              }),
                              autoActivate: 'true',
                            },
                          });
                        }
                      : undefined,
                    onCtaLinkPress: resolvedCtaLinkLabel
                      ? () =>
                          rewardsActionNavigation(
                            item,
                            section.name,
                            coordinates,
                          )
                      : undefined,
                  };
                }),
            },
          },
        ];
      case SectionStyleType.NearbySection:
        return nearbyRenderer ? nearbyRenderer(section) : [];
      case SectionStyleType.MinimalGridCarousel:
        return (section.itemsV2 ?? [])
          .filter(
            (item): item is SectionItems =>
              item.__typename ===
              PerkSectionsDataItemsResultTypenames.SectionItems,
          )
          .slice(
            0,
            (section.itemsV2?.length ?? 0) > REWARDS_HOME.minimalGridMaxItems
              ? REWARDS_HOME.minimalGridMaxItems
              : (section.itemsV2?.length ?? 0),
          )
          .map((item) => minimalGridListItem(item, section.name, coordinates));
      default:
        return [];
    }
  };

  return sections.map((section) => ({
    sectionHeader: {
      id: `sectionHeaderId${section.id}`,
      name: VXLComponentId.PRESSABLE_SECTION_HEADER_V2,
      props: {
        sectionId: section.id,
        sectionName: section.name,
        sectionStyle: section.sectionStyleType,
        hasNext: section.hasNext,
      },
    },
    sectionFooter: undefined,
    data: rewardsSections(section),
  }));
};

export const formatRewardsData = (
  selfFundedOffersSection: SectionedContentScreenSection[],
  sections?: PerkSectionsData[],
  topCategoriesData?: Query,
  cashbackEarnedData?: Query,
  activatedOffersData?: Query,
  nearbyRenderer?: (section: PerkSectionsData) => VXLNode[],
  coordinates?: google.maps.LatLngLiteral,
  promoSectionData?: Maybe<PromoSectionData>,
  activateButtonLabel?: string,
  translatedText?: (key: string, options?: Record<string, string>) => string,
) => {
  return {
    sectionedContentModel: {
      sections: [
        buildPromoCardSection(promoSectionData),
        buildCashbackSection(cashbackEarnedData),
        buildActivatedOffersSection(activatedOffersData, coordinates),
        ...selfFundedOffersSection,
        ...buildPerkSections(
          sections,
          coordinates,
          activateButtonLabel,
          nearbyRenderer,
          translatedText,
        ),
        buildTopCategoriesSection(topCategoriesData),
      ],
    },
  };
};
